import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { PageRequest } from '@/core/request/PageRequest';

import {
  ListCreditsSupplierQuery,
  ListSuppliersQuery,
  Supplier,
  SupplierReduced,
} from './Suppliers.types';

import {
  deleteSupplier,
  createSupplier,
  updateSupplier,
  getSupplier,
  activeSupplier,
  listCreditsSupplier,
  listSuppliersReduced,
  getSupplierReduced,
} from './Suppliers.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import replaceWords from '@/utils/functions/replaceWords';
import verifyIfHasVinculed from '@/utils/functions/verifyIfHasVinculed';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useSuppliersReduce(
  params?: ListSuppliersQuery,
  enabled?: boolean,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['suppliers/reduced', { ...params }]);

  return useInfiniteQuery(
    ['suppliers/reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listSuppliersReduced(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useSuppliersReducedMutation() {
  return useMutation(listSuppliersReduced, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useSupplier(id?: number) {
  const cache = queryClient.getQueryData(['supplier', id]);

  return useQuery(['supplier', id], () => (id ? getSupplier(id) : null), {
    enabled: !!id && !cache,
    onError(error) {
      notificationError(error);
    },
  });
}

export function useSupplierMutation() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<Supplier>(['supplier', id]);

      if (cache) return cache;

      return await getSupplier(id);
    },
    {
      onError(error) {
        notificationError(error);
      },
      onSuccess(data, id) {
        queryClient.setQueryData(['supplier', id], data);
      },
    },
  );
}

export function useSupplierReduced(id?: number) {
  const cache = queryClient.getQueryData(['supplier/reduced', id]);

  return useQuery(
    ['supplier/reduced', id],
    () => (id ? getSupplierReduced(id) : null),
    {
      enabled: !!id && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useSupplierReducedMutation() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<SupplierReduced>([
        'supplier/reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getSupplierReduced(id);

      return data;
    },
    {
      onError(error) {
        notificationError(error);
      },
      onSuccess(data, id) {
        queryClient.setQueryData(['supplier/reduced', id], data);
      },
    },
  );
}

export function useCreateSupplier() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((obj: Supplier) => createSupplier(obj), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'suppliers.notifications.create.success.title',
        }),
        description: intl.formatMessage({
          id: 'suppliers.notifications.create.success.description',
        }),
      });

      queryChanel({ removeQueries: [['suppliers/reduced']] });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useUpdateSupplier() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation((obj: Supplier) => updateSupplier(obj), {
    async onSuccess(data) {
      notification.success({
        message: intl.formatMessage({
          id: 'suppliers.notifications.update.success.title',
        }),
        description: intl.formatMessage({
          id: 'suppliers.notifications.update.success.description',
        }),
      });

      queryChanel({
        removeQueries: [
          ['suppliers/reduced'],
          ['supplier', data.id],
          ['supplier/reduced', data.id],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useDeleteSupplier() {
  const queryChanel = useQueryChanel();

  const intl = useIntl();
  const mutation = useMutation((id: number) => deleteSupplier(id), {
    async onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'suppliers.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'suppliers.remove.notification.successDescription',
        }),
      });

      queryChanel({
        removeQueries: [['suppliers/reduced']],
      });
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            verifyIfHasVinculed(err) ? replaceWords(err) : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveSupplier() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  return useMutation(
    (obj: { id: number; activeSupplier: boolean }) => activeSupplier(obj),
    {
      onSuccess(_, data) {
        message.success(
          intl.formatMessage({
            id: `suppliers.${
              data.activeSupplier ? 'inactivate' : 'activate'
            }.message.success`,
          }),
        );

        queryChanel({
          removeQueries: [
            ['suppliers/reduced'],
            ['supplier', data.id],
            ['supplier/reduced', data.id],
          ],
        });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useSupplierCredits(params?: ListCreditsSupplierQuery) {
  return useInfiniteQuery(
    ['suppliers-credit', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listCreditsSupplier(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: !!params?.supplierId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}
